import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RiCheckboxCircleLine, RiCloseLine, RiComputerLine, RiHistoryLine } from 'react-icons/ri';
import deviceService from '../../services/deviceService';
import deviceModelService, { DeviceModel } from '../../services/deviceModelService';
import supplierService from '../../services/supplierService';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useUpdateDevice } from '../../hooks/mutations/useDeviceMutations';
import { useActiveBrands } from '../../hooks/useBrands';
import { useActiveDeviceTypes } from '../../hooks/useDeviceTypes';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryClient, queryKeys } from '../../queryClient';
import { Device } from '../../types/device';
import { DEVICES_PATH, deviceDetailUrl } from '../../utils/deviceLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import DeviceFormFields from '../../components/device/DeviceFormFields';
import {
  DeviceForm,
  DEVICE_FIELD_LABELS,
  DEVICE_VALUE_FORMATTERS,
  buildDevicePayload,
  deviceToForm,
  emptyDeviceForm,
  parseSerialNumbers,
  validateDeviceForm,
} from '../../components/device/deviceForm';
import {
  DetailPageLayout,
  DetailSection,
  EntityActivityTimeline,
  RecordMetaCard,
  useDetailNavigation,
} from '../../components/detail';

/**
 * หน้าเพิ่ม/แก้ไขอุปกรณ์ — `/master-data/devices/new` · `/master-data/devices/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน DevicePage · มติ PO 28 ต.ค. 2026 · ต้นแบบ CompanyFormPage)
 *
 * ตรรกะบันทึกยกมาจาก handleSaveDevice ของโมดัลเดิมครบ:
 * - กันกดซ้ำด้วย ref จน mutation settle (KD3 S2126 — ของนับจำนวน POST ซ้ำ = restock บวกยอดซ้ำ)
 * - สร้าง: หลาย serial → POST /devices/bulk (ATOMIC) · serial เดียว → POST /devices · รหัสซ้ำ → ออกรหัสใหม่ + เตือน รอกดอีกครั้ง
 * - แก้ไข: If-Match (409 → โหลดค่าล่าสุดลงฟอร์มใหม่ ไม่ปล่อยให้กดทับงานคนอื่น) · imei ส่งเฉพาะเมื่อแก้จริง (C8)
 * - ยอด/สถานะเปลี่ยนผ่าน PUT /devices/{id} เท่านั้น — backend เขียน ledger stock_movements ให้ (ไม่มีเส้นใหม่)
 */
const DeviceFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const mode = isEdit ? 'edit' : 'create';
  const deviceId = isEdit ? parseRouteId(id) : null;
  const { canView, hasPermission } = usePermission();
  const { backTo, goTo } = useDetailNavigation(DEVICES_PATH);
  const updateDeviceMutation = useUpdateDevice();

  const [formData, setFormData] = useState<DeviceForm>(() => emptyDeviceForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeError, setCodeError] = useState<string>('');
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [loadingDeviceModels, setLoadingDeviceModels] = useState<boolean>(false);

  const deviceQuery = useQuery({
    queryKey: queryKeys.devices.detail(deviceId ?? 0),
    queryFn: () => deviceService.getDevice(deviceId!),
    enabled: isEdit && deviceId !== null,
    // หน้าแก้ไขต้องได้ token If-Match ล่าสุดเสมอ — ไม่ใช้ cache เก่าจากหน้าดู
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const selectedDevice: Device | undefined = deviceQuery.data;

  // KD3 S2470: lookup ยี่ห้อ/ผู้จำหน่ายยิงเฉพาะเมื่อมีสิทธิ์ (role ที่มีแค่ devices.* ไม่ได้ 403 ซ้ำ ๆ)
  const { data: brandsData } = useActiveBrands({ enabled: canView('brands') });
  const { data: deviceTypesData } = useActiveDeviceTypes();
  const brands = brandsData || [];
  const deviceTypes = deviceTypesData?.items || [];
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => supplierService.getActiveSuppliers(),
    enabled: canView('suppliers'),
  });
  const suppliers = suppliersData || [];

  const loadModelsForBrand = async (brandId: number) => {
    setLoadingDeviceModels(true);
    try {
      const models = await deviceModelService.getByBrand(brandId);
      setDeviceModels(models || []);
    } catch (error) {
      console.error('Error loading device models:', error);
      setDeviceModels([]);
    } finally {
      setLoadingDeviceModels(false);
    }
  };

  // ตั้งค่าฟอร์มครั้งเดียวต่อเครื่อง (refetch ภายหลังไม่ทับสิ่งที่ผู้ใช้กำลังพิมพ์)
  const initializedFor = useRef<number | 'new' | null>(null);
  useEffect(() => {
    if (isEdit) {
      const device = selectedDevice;
      if (!device || initializedFor.current === device.id) return;
      initializedFor.current = device.id;
      const f = deviceToForm(device);
      setFormData(f);
      setInitialSnapshot(JSON.stringify(f));
      // โหลด device models ของ brand ที่เลือกอยู่
      // KD3 S2503: ยิงเฉพาะเมื่อมีสิทธิ์ device_models.view (403 ทุกครั้งที่เปิดถ้าไม่ gate)
      if (device.brand_id && canView('device_models')) {
        loadModelsForBrand(device.brand_id);
      } else {
        setDeviceModels([]);
      }
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง (รหัสจริงออกตอนบันทึก)
    codeGeneratorService
      .getNextCodePreview('device', true)
      .then((r) => r.next_code)
      .catch((error) => {
        console.error('Error getting next code preview:', error);
        return '';
      })
      .then((code) => {
        const f = emptyDeviceForm(code);
        setFormData(f);
        setInitialSnapshot(JSON.stringify(f));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, selectedDevice]);

  const handleBrandChange = async (value: number | string | null) => {
    setFormData((prev) => ({ ...prev, brand_id: value, device_model_id: null, model: '' }));
    setDeviceModels([]);
    if (value && canView('device_models')) {
      await loadModelsForBrand(Number(value));
    }
  };

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify(formData) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);

  const cancelTo = isEdit && deviceId ? deviceDetailUrl(deviceId) : backTo;

  /** หลังบันทึก: ไปหน้าดู (replace) · ไม่มีสิทธิ์ดู = กลับที่เปิดมา */
  const afterSaved = (savedId?: number | null) => {
    setSaved(true); // ปลดตัวกันออกจากหน้า ก่อนเปลี่ยนหน้า
    if (savedId && hasPermission('devices.view')) goTo(deviceDetailUrl(savedId), { replace: true });
    else goTo(backTo, { replace: true });
  };

  const createDeviceMutation = useMutation({
    mutationFn: (data: any) => deviceService.createDevice(data),
    onSuccess: (created: Device) => {
      showAlert('สำเร็จ', 'สร้างอุปกรณ์สำเร็จ', 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
      afterSaved(created?.id);
    },
    onError: async (error: any) => {
      console.error('Error creating device:', error);
      // S1218 FIX (BUG-S1218-003): handle 422 field_errors inline FIRST · before string-match fallback
      const fieldErrs = mapFieldErrors(error);
      if (fieldErrs) {
        setFormErrors(fieldErrs);
        focusFirstInvalidField(fieldErrs);
        return;
      }
      const errorMessage = error.response?.data?.error?.user_message ||
                           error.response?.data?.error?.message ||
                           error.response?.data?.detail || '';
      if (typeof errorMessage === 'string' && errorMessage.includes('รหัสอุปกรณ์นี้มีอยู่ในระบบแล้ว')) {
        // รหัสซ้ำ → สร้างรหัสใหม่ แสดงเตือนใต้ช่อง รอผู้ใช้กดบันทึกอีกครั้ง (ไม่บันทึกอัตโนมัติ)
        const oldCode = formData.code;
        try {
          const newCode = await codeGeneratorService.generateSingleCode('device');
          setFormData((prev) => ({ ...prev, code: newCode }));
          setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
        } catch (genError) {
          console.error('Error generating new code:', genError);
          setCodeError('ไม่สามารถสร้างรหัสใหม่ได้ กรุณาลองใหม่');
        }
      } else if (typeof errorMessage === 'string' && /serial/i.test(errorMessage)) {
        // user bug Tirawat (Support) 2026-08-26: serial ซ้ำ → ข้อความจริงจาก backend ใต้ช่อง Serial + toast
        const fe = { ...formErrors, serial_number: errorMessage };
        setFormErrors(fe);
        focusFirstInvalidField(fe);
        showAlert('บันทึกไม่ได้ — Serial Number ซ้ำ', errorMessage, 'error');
      } else {
        // ข้อความจริงจาก backend (IMEI ซ้ำ ฯลฯ) — ห้ามกลืนเป็นข้อความกลางๆ
        showAlert('เกิดข้อผิดพลาด', (typeof errorMessage === 'string' && errorMessage) || 'ไม่สามารถบันทึกข้อมูลได้', 'error');
      }
    },
  });

  // Bulk create: สร้างอุปกรณ์หลายเครื่องจากหลาย serial ครั้งเดียว (ATOMIC ฝั่ง backend)
  const createDevicesBulkMutation = useMutation({
    mutationFn: (data: any) => deviceService.createDevicesBulk(data),
    onSuccess: (result: any) => {
      showAlert('สำเร็จ', `สร้างอุปกรณ์ ${result?.created_count ?? 0} เครื่องสำเร็จ`, 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
      // หลายเครื่อง = ไม่มีหน้าดูเครื่องเดียวให้ไป → กลับหน้ารายการ
      setSaved(true);
      goTo(backTo, { replace: true });
    },
    onError: (error: any) => {
      console.error('Error bulk-creating devices:', error);
      const details = error?.response?.data?.error?.details;
      const tooShort: string[] = details?.too_short || [];
      const dupBatch: string[] = details?.duplicate_in_batch || [];
      const exists: string[] = details?.already_exists || [];
      // serial → "รหัสเครื่อง (สถานะ)" จาก backend — บอกว่าซ้ำกับเครื่องไหน (Tirawat 2026-08-26)
      const existsDevices: Record<string, string> = details?.already_exists_devices || {};
      if (tooShort.length || dupBatch.length || exists.length) {
        const parts: string[] = [];
        if (exists.length) parts.push(`• ซ้ำกับของเดิม: ${exists.map((sn) => existsDevices[sn] ? `${sn} → ${existsDevices[sn]}` : sn).join(', ')}`);
        if (dupBatch.length) parts.push(`• ซ้ำในรายการที่กรอก: ${dupBatch.join(', ')}`);
        if (tooShort.length) parts.push(`• สั้นเกินไป (ต้อง ≥ 3 ตัว): ${tooShort.join(', ')}`);
        showAlert('พบ Serial ที่มีปัญหา — ยังไม่ได้สร้างอุปกรณ์ใดเลย', parts.join('\n'), 'error');
      } else {
        showAlert('เกิดข้อผิดพลาด', error?.response?.data?.error?.user_message || 'ไม่สามารถสร้างอุปกรณ์ได้', 'error');
      }
    },
  });

  // KD3 S2126 (BUG-S2126-002): ดับเบิลคลิก "บันทึก" ยิง POST 2 ครั้ง — ถือ ref ไว้จนกว่า mutation จะ settle
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const releaseSave = () => { savingRef.current = false; setIsSaving(false); };

  const handleSaveDevice = async () => {
    if (savingRef.current) return;
    const errors = validateDeviceForm(formData, mode, selectedDevice);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTimeout(() => {
        const el = document.querySelector(`[name="${Object.keys(errors)[0]}"]`) as HTMLInputElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }, 50);
      return;
    }
    setFormErrors({});
    savingRef.current = true;
    setIsSaving(true);
    let handedOff = false;

    try {
      const deviceFormData = buildDevicePayload(formData, mode, selectedDevice);

      if (mode === 'create') {
        // ติดตาม Serial: รองรับกรอกหลาย serial ครั้งเดียว (textarea, คั่น , หรือขึ้นบรรทัด)
        if (formData.is_serialized) {
          const serials = parseSerialNumbers(formData.serial_number);
          if (serials.length === 0) {
            setFormErrors((prev) => ({ ...prev, serial_number: 'กรุณาใส่หมายเลขซีเรียลอย่างน้อย 1 รายการ' }));
            return;
          }
          if (serials.length > 1) {
            // BULK: ส่ง serial_numbers + field ร่วม (ตัด serial_number/code/imei ออก —
            // backend สร้างรหัสให้แต่ละเครื่อง · DeviceBulkCreate ไม่รองรับ imei โดยตั้งใจ)
            const { serial_number, code, imei, ...shared } = deviceFormData as any;
            handedOff = true;
            createDevicesBulkMutation.mutate({ ...shared, serial_numbers: serials }, { onSettled: releaseSave });
            return;
          }
          // serial เดียว: ใช้ค่าที่ parse แล้ว (สะอาด ไม่มี whitespace/บรรทัดเกิน)
          (deviceFormData as any).serial_number = serials[0];
        }
        handedOff = true;
        createDeviceMutation.mutate(deviceFormData as any, { onSettled: releaseSave });
      } else if (selectedDevice) {
        handedOff = true;
        updateDeviceMutation.mutate({
          id: selectedDevice.id,
          data: deviceFormData as any,
          // BUG-S162-P1 rollout (S413): token ของแถวตอนเปิดฟอร์ม — มีคนแก้คั่นกลางจะได้ 409 แทนการทับ
          ifMatch: (selectedDevice as any).updated_at || (selectedDevice as any).created_at,
        }, {
          onSettled: releaseSave,
          onSuccess: () => {
            setCodeError('');
            afterSaved(selectedDevice.id);
          },
          onError: (error: any) => {
            // P-S601-1: 409 stale-edit — useMutationWithRefresh แจ้งเตือนแล้ว · โหลดค่าล่าสุดลงฟอร์มใหม่ (token ใหม่)
            // ห้ามคงค่าที่พิมพ์ไว้แล้วให้กดซ้ำ = เขียนทับงานของอีกคนโดยไม่เห็นว่าเขาแก้อะไร
            if (error.response?.status === 409) {
              initializedFor.current = null;
              deviceQuery.refetch();
            }
          },
        });
      }
    } catch (error) {
      console.error('Error saving device:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถบันทึกข้อมูลอุปกรณ์ได้'), 'error');
    } finally {
      if (!handedOff) releaseSave();
    }
  };

  const title = isEdit ? 'แก้ไขข้อมูลอุปกรณ์' : 'เพิ่มอุปกรณ์ใหม่';
  const breadcrumbs = useMemo(() => [
    { label: 'อุปกรณ์', path: backTo },
    ...(isEdit && selectedDevice ? [{ label: selectedDevice.code || `#${selectedDevice.id}`, path: deviceDetailUrl(selectedDevice.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, selectedDevice]);

  const notFound = isEdit && deviceId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiComputerLine />}
      title={title}
      recordId={isEdit ? selectedDevice?.code || deviceId : undefined}
      subtitle={isEdit ? selectedDevice?.serial_number || undefined : 'กรอกข้อมูลอุปกรณ์ (รหัสสร้างอัตโนมัติ)'}
      backTo={cancelTo}
      loading={(isEdit && deviceQuery.isLoading) || (initialSnapshot === null && !notFound && !deviceQuery.isError)}
      error={notFound || deviceQuery.error}
      notFoundMessage="ไม่พบอุปกรณ์ที่ระบุ"
      onRetry={() => deviceQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && selectedDevice ? (
          <>
            <RecordMetaCard
              createdAt={selectedDevice.created_at}
              createdBy={selectedDevice.created_by}
              updatedAt={selectedDevice.updated_at}
              updatedBy={selectedDevice.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="device"
                entityId={selectedDevice.id}
                fieldLabels={DEVICE_FIELD_LABELS}
                valueFormatters={DEVICE_VALUE_FORMATTERS}
              />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลอุปกรณ์" icon={<RiComputerLine />}>
        {/* ไม่ใช้ <form onSubmit>: Enter ในช่องค้นหาของ SearchableSelect จะกลายเป็นกดบันทึก (โมดัลเดิมก็ไม่ใช้ native submit) */}
        <div>
          <DeviceFormFields
            mode={mode}
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            codeError={codeError}
            setCodeError={setCodeError}
            deviceTypes={deviceTypes}
            brands={brands}
            suppliers={suppliers}
            deviceModels={deviceModels}
            loadingDeviceModels={loadingDeviceModels}
            onBrandChange={handleBrandChange}
            original={selectedDevice ?? null}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSaveDevice}
              disabled={isSaving}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {isSaving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default DeviceFormPage;
